import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';
import { EntityTypeResponse, getEntityTypes } from '../apiClient/entityType';
import { FilingStatus, getFilingStatuses } from '../apiClient/filingStatuses';
import { BillingType, getBillingTypes } from '../apiClient/billingTypes';
import {
  ContactRole,
  ContactType,
  getContactRoles,
  getContactTypes,
} from '../apiClient/contacts';
import {
  getOutsideProviderTypes,
  OutsideProviderType,
} from '../apiClient/outsideProviders';
import {
  getResidences,
  getStateTypes,
  StateResidence,
  StateType,
} from '../apiClient/states';
import {
  getRelationshipActivityTypes,
  getRelationshipTypes,
  RelationshipActivityType,
  RelationshipType,
} from '../apiClient/relationships';
import {
  getTemplateClassifications,
  TemplateClassification,
} from '../apiClient/templateClassifications';
import {
  AssignmentType,
  getAssignmentTypes,
} from '../apiClient/assignmentTypes';
import { getTemplateTypes, TemplateType } from '../apiClient/templateTypes';
import { FilingType, getFilingTypes } from '../apiClient/filingTypes';
import {
  BasedOn,
  getBasedOns,
  getRecurrenceFrequencies,
  RecurrenceFrequency,
} from '../apiClient/recurrenceSettings';
import {
  getRecurrenceGenerateBefores,
  RecurrenceGenerateBefore,
} from '../apiClient/recurrenceSettings';
import {
  getTaxPaymentTypes,
  TaxPaymentType,
} from '../apiClient/taxPaymentTypes';
import {
  getOverpaymentSettings,
  OverpaymentSetting,
} from '../apiClient/overpaymentSettings';
import { getPermissions, Permission } from '../apiClient/permissions';
import { getPriceStatuses, PriceStatusesResponse } from '../apiClient/client';

const globalOptionsAtoms = {
  entityTypes: atom<EntityTypeResponse | null>(null),
  filingStatus: atom<FilingStatus[]>([]),
  billingTypes: atom<BillingType[]>([]),
  contactTypes: atom<ContactType[]>([]),
  contactRoles: atom<ContactRole[]>([]),
  outsideProviderTypes: atom<OutsideProviderType[]>([]),
  stateResidences: atom<StateResidence[]>([]),
  relationshipAcitivityTypes: atom<RelationshipActivityType[]>([]),
  relationshipTypes: atom<RelationshipType[]>([]),
  templateClassification: atom<TemplateClassification[]>([]),
  assigmentTypes: atom<AssignmentType[]>([]),
  templateTypes: atom<TemplateType[]>([]),
  filingTypes: atom<FilingType[]>([]),
  recurrenceFrequencies: atom<RecurrenceFrequency[]>([]),
  recurrenceGenerateBefores: atom<RecurrenceGenerateBefore[]>([]),
  basedOns: atom<BasedOn[]>([]),
  optionsAreLoaded: atom(false),
};

let numberOfParallelRequestsInProgress = 0;

export function useGlobalOptions() {
  const [optionsAreLoaded, setOptionsAreLoaded] = useAtom(
    globalOptionsAtoms.optionsAreLoaded
  );

  const [stateResidences, setStateResidences] = useAtom(
    globalOptionsAtoms.stateResidences
  );
  const [billingTypes, setBillingTypes] = useAtom(
    globalOptionsAtoms.billingTypes
  );
  const [contactTypes, setContactTypes] = useAtom(
    globalOptionsAtoms.contactTypes
  );
  const [contactRoles, setContactRoles] = useAtom(
    globalOptionsAtoms.contactRoles
  );
  const [outsideProviderTypes, setOutsideProviderTypes] = useAtom(
    globalOptionsAtoms.outsideProviderTypes
  );
  const [entityTypes, setEntityTypes] = useAtom(globalOptionsAtoms.entityTypes);
  const [filingStatuses, setFilingStatuses] = useAtom(
    globalOptionsAtoms.filingStatus
  );
  const [relationshipActivityTypes, setRelationshipActivityTypes] = useAtom(
    globalOptionsAtoms.relationshipAcitivityTypes
  );
  const [relationshipTypes, setRelationshipTypes] = useAtom(
    globalOptionsAtoms.relationshipTypes
  );
  const [templateClassifications, setTemplateClassifications] = useAtom(
    globalOptionsAtoms.templateClassification
  );
  const [assignmentTypes, setAssignmentTypes] = useAtom(
    globalOptionsAtoms.assigmentTypes
  );
  const [templateTypes, setTemplateTypes] = useAtom(
    globalOptionsAtoms.templateTypes
  );
  const [filingTypes, setFilingTypes] = useAtom(globalOptionsAtoms.filingTypes);
  const [recurrenceFrequencies, setRecurrenceFrequencies] = useAtom(
    globalOptionsAtoms.recurrenceFrequencies
  );
  const [recurrenceGenerateBefores, setRecurrenceGenerateBefores] = useAtom(
    globalOptionsAtoms.recurrenceGenerateBefores
  );
  const [basedOns, setBasedOns] = useAtom(globalOptionsAtoms.basedOns);

  function loadStateResidences() {
    return getResidences().then((res) => {
      setStateResidences(res.results);
    });
  }

  function loadBillingTypes() {
    return getBillingTypes().then((res) => {
      setBillingTypes(res.results);
    });
  }

  function loadContactTypes() {
    return getContactTypes().then((res) => {
      setContactTypes(res);
    });
  }

  function loadContactRoles() {
    return getContactRoles().then((res) => {
      setContactRoles(res);
    });
  }

  function loadOutsideProviderTypes() {
    return getOutsideProviderTypes().then((res) => {
      setOutsideProviderTypes(res);
    });
  }

  function loadEntityTypes() {
    return getEntityTypes().then((res) => {
      setEntityTypes(res);
    });
  }

  function loadFilingStatuses() {
    return getFilingStatuses().then((res) => {
      setFilingStatuses(res.results);
    });
  }

  function loadRelationshipActivityTypes() {
    return getRelationshipActivityTypes().then((res) => {
      setRelationshipActivityTypes(res);
    });
  }

  function loadRelationshipTypes() {
    return getRelationshipTypes().then((res) => {
      setRelationshipTypes(res);
    });
  }

  function loadFilingTypes() {
    return getFilingTypes().then((res) => {
      setFilingTypes(res);
    });
  }

  const fetchTemplateClassifications = async () => {
    const response = await getTemplateClassifications();
    setTemplateClassifications(response);
  };

  const fetchAssignmentTypes = async () => {
    const response = await getAssignmentTypes();
    setAssignmentTypes(response);
  };

  const fetchTemplateTypes = async () => {
    const response = await getTemplateTypes();
    setTemplateTypes(response);
  };

  const loadRecurenceFrequencies = async () => {
    const response = await getRecurrenceFrequencies();
    setRecurrenceFrequencies(response);
  };

  const loadRecurrenceGenerateBefores = async () => {
    const response = await getRecurrenceGenerateBefores();
    setRecurrenceGenerateBefores(response);
  };

  const loadBasedOns = async () => {
    const response = await getBasedOns();
    setBasedOns(response);
  };

  const loadAllOptions = () => {
    if (numberOfParallelRequestsInProgress > 0) return;
    numberOfParallelRequestsInProgress++;
    Promise.all([
      loadStateResidences(),
      loadBillingTypes(),
      loadContactTypes(),
      loadContactRoles(),
      loadOutsideProviderTypes(),
      loadEntityTypes(),
      loadFilingStatuses(),
      loadRelationshipActivityTypes(),
      loadRelationshipTypes(),
      fetchTemplateClassifications(),
      fetchAssignmentTypes(),
      fetchTemplateTypes(),
      loadFilingTypes(),
      loadRecurrenceGenerateBefores(),
      loadRecurenceFrequencies(),
      loadBasedOns(),
    ])
      .then(() => setOptionsAreLoaded(true))
      .finally(() => {
        numberOfParallelRequestsInProgress--;
      });
  };

  useEffect(() => {
    if (optionsAreLoaded) return;
    loadAllOptions();
  }, []);

  return {
    refreshGlobalOptions: loadAllOptions,
    stateResidences,
    billingTypes,
    contactTypes,
    contactRoles,
    outsideProviderTypes,
    entityTypes,
    filingStatuses,
    relationshipActivityTypes,
    relationshipTypes,
    templateClassifications,
    assignmentTypes,
    templateTypes,
    filingTypes,
    recurrenceFrequencies,
    recurrenceGenerateBefores,
    basedOns,
    optionsAreLoaded,
  };
}

const permissionsAtoms = {
  permissions: atom<Permission[]>([]),
  permissionsAreLoaded: atom(false),
};

let permissionsRequestInProgress = false;

export function usePermissions() {
  const [permissions, setPermissions] = useAtom(permissionsAtoms.permissions);
  const [permissionsAreLoaded, setPermissionsAreLoaded] = useAtom(
    permissionsAtoms.permissionsAreLoaded
  );

  const loadPermissions = () => {
    if (permissionsRequestInProgress) return;
    permissionsRequestInProgress = true;
    getPermissions()
      .then((res) => {
        setPermissions(res.results);
        setPermissionsAreLoaded(true);
      })
      .finally(() => {
        permissionsRequestInProgress = false;
      });
  };

  useEffect(() => {
    if (permissionsAreLoaded) return;
    loadPermissions();
  }, []);

  return {
    refreshPermissions: loadPermissions,
    permissions,
    permissionsAreLoaded,
  };
}

const stateTypesAtoms = {
  stateTypes: atom<StateType[]>([]),
  stateTypesAreLoaded: atom(false),
};

let stateTypesRequestInProgress = false;

export function useStateTypes() {
  const [stateTypes, setStateTypes] = useAtom(stateTypesAtoms.stateTypes);
  const [stateTypesAreLoaded, setStateTypesAreLoaded] = useAtom(
    stateTypesAtoms.stateTypesAreLoaded
  );

  const loadStateTypes = () => {
    if (stateTypesRequestInProgress) return;
    stateTypesRequestInProgress = true;
    getStateTypes()
      .then((res) => {
        setStateTypes(res.results);
        setStateTypesAreLoaded(true);
      })
      .finally(() => {
        stateTypesRequestInProgress = false;
      });
  };

  useEffect(() => {
    if (stateTypesAreLoaded) return;
    loadStateTypes();
  }, []);

  return {
    refreshStateTypes: loadStateTypes,
    stateTypes,
    stateTypesAreLoaded,
  };
}

const priceStatusesAtoms = {
  priceStatuses: atom<PriceStatusesResponse>([]),
  priceStatusesAreLoaded: atom(false),
};

let priceStatusesRequestInProgress = false;

export function usePriceStatuses() {
  const [priceStatuses, setPriceStatuses] = useAtom(
    priceStatusesAtoms.priceStatuses
  );
  const [priceStatusesAreLoaded, setPriceStatusesAreLoaded] = useAtom(
    priceStatusesAtoms.priceStatusesAreLoaded
  );

  const loadPriceStatuses = () => {
    if (priceStatusesRequestInProgress) return;
    priceStatusesRequestInProgress = true;
    getPriceStatuses()
      .then((res) => {
        setPriceStatuses(res);
        setPriceStatusesAreLoaded(true);
      })
      .finally(() => {
        priceStatusesRequestInProgress = false;
      });
  };

  useEffect(() => {
    if (priceStatusesAreLoaded) return;
    loadPriceStatuses();
  }, []);

  return {
    refreshPriceStatuses: loadPriceStatuses,
    priceStatuses,
    priceStatusesAreLoaded,
  };
}

const taxPaymentTypesAtoms = {
  taxPaymentTypes: atom<TaxPaymentType[]>([]),
  taxPaymentTypesAreLoaded: atom(false),
};

let taxPaymentTypesRequestInProgress = false;

export function useTaxPaymentTypes() {
  const [taxPaymentTypes, setTaxPaymentTypes] = useAtom(
    taxPaymentTypesAtoms.taxPaymentTypes
  );
  const [taxPaymentTypesAreLoaded, setTaxPaymentTypesAreLoaded] = useAtom(
    taxPaymentTypesAtoms.taxPaymentTypesAreLoaded
  );

  const loadTaxPaymentTypes = () => {
    if (taxPaymentTypesRequestInProgress) return;
    taxPaymentTypesRequestInProgress = true;
    getTaxPaymentTypes()
      .then((res) => {
        setTaxPaymentTypes(res);
        setTaxPaymentTypesAreLoaded(true);
      })
      .finally(() => {
        taxPaymentTypesRequestInProgress = false;
      });
  };

  useEffect(() => {
    if (taxPaymentTypesAreLoaded) return;
    loadTaxPaymentTypes();
  }, []);

  return {
    refreshTaxPaymentTypes: loadTaxPaymentTypes,
    taxPaymentTypes,
    taxPaymentTypesAreLoaded,
  };
}

const overpaymentSettingsAtoms = {
  overpaymentSettings: atom<OverpaymentSetting[]>([]),
  overpaymentSettingsAreLoaded: atom(false),
};

let overpaymentSettingsRequestInProgress = false;

export function useOverpaymentSettings() {
  const [overpaymentSettings, setOverpaymentSettings] = useAtom(
    overpaymentSettingsAtoms.overpaymentSettings
  );
  const [overpaymentSettingsAreLoaded, setOverpaymentSettingsAreLoaded] =
    useAtom(overpaymentSettingsAtoms.overpaymentSettingsAreLoaded);

  const loadOverpaymentSettings = () => {
    if (overpaymentSettingsRequestInProgress) return;
    overpaymentSettingsRequestInProgress = true;
    getOverpaymentSettings()
      .then((res) => {
        setOverpaymentSettings(res);
        setOverpaymentSettingsAreLoaded(true);
      })
      .finally(() => {
        overpaymentSettingsRequestInProgress = false;
      });
  };

  useEffect(() => {
    if (overpaymentSettingsAreLoaded) return;
    loadOverpaymentSettings();
  }, []);

  return {
    refreshOverpaymentSettings: loadOverpaymentSettings,
    overpaymentSettings,
    overpaymentSettingsAreLoaded,
  };
}
